<template>
  <OnClickOutside @trigger="openDrop = false">
    <div class="drop-multiple-header" @click="openDropContainer" :class="{ active: openDrop, rotate: rotated }" :style="{ width: `${width}`, height: `${height}` }">
      <div class="drop-title" :style="{ borderColor }">
          <span><slot></slot></span>
          <div class="selected-count" v-if="selectedAmount > 0">
            {{ selectedAmount }}
          </div>
          <Icon class="drop-icon" name="arrow_down" />
      </div>

      <Transition>
        <div v-if="openDrop" @click.stop class="drop-options" :style="{ width: `${selectWidth}`, top: !rotated ? `${height}` : '', bottom: rotated ? `${height}` : '' }">
          <div class="select-input" v-show="searchable">
            <input ref="search" type="text" :placeholder="$t('button.search')" class="search-bar" @input="handleSearch($event.target.value)" />
          </div>

          <div class="drop-list">
            <div class="no-results" v-if="!computedItems.length">{{ $t('misc.noResults') }}</div>

            <div v-for="(group, index) in computedItems" :key="index" class="group-category" @click="toggleGroup(group)">
              <div class="category-title" :class="{ hidden: !group.show }">
                <span class="group-name">{{ group?.translation ? group.translation : group?.name }}</span>
                <span class="item-count me-auto">({{ group.list.length }})</span>
                <!-- how many selected in this group -->
                <span class="selected-count" v-if="getSelectedCount(group.name)">{{ getSelectedCount(group.name) }}</span>
                <Icon :name="group.show ? 'arrow_up' : 'arrow_down'" />
                </div>
              <div class="category-items" v-if="group.show">
                <div v-for="item in group.list" :key="item.value" :title="item.name" class="drop-item" @click="selectOption(item)" @click.stop>
                  <div class="select-svg" v-if="selectedValues.includes(item.value)"> 
                    <Icon name="checkbox_active" />
                  </div>
                  <div class="select-svg" v-else>
                    <Icon name="checkbox" />
                  </div>
                  <span class="prop-name" :class="{ active: selectedValues.includes(item.value) }">
                      <template v-if="item?.color"><div class="color-dot" :style="{ backgroundColor: item.color }"></div></template>
                      <span>{{ item.name }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="drop-actions" v-if="!noFooter">
            <div class="drop-buttons">
              <button type="button" @click="selectedValues = []">{{ $t('button.clearAll') }}</button>
              <button type="button" @click="saveList(selectedValues)">{{ $t('button.save') }}</button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </OnClickOutside>
</template>

<script>
import { OnClickOutside } from "@vueuse/components";

export default {
  components: { OnClickOutside },
  emits: ["update:selected", "optionChanged"],
  props: {
    searchable: { type: Boolean, default: true },
    borderColor: { default: "#dbdbdb" },
    width: { default: "160px" },
    height: { default: "35px" },
    selectWidth: { default: "inherit" },
    slots: { default: false },
    selected: { type: Array, default: () => [] },
    list: { type: Array, default: () => [], validate: (val) => Array.isArray(val) && val.length > 0 },
    noFooter: { default: false },
    autoRotate: { default: false },
    lazyCaller: { default: true }
  },
  data() {
    return {
      search: "",
      openDrop: false,
      rotated: false,
      values: [],
      flatList: [],
      selectedValues: []
    };
  },
  methods: {
    toggleGroup(group) {
      // Find the original group in this.values and toggle its show property and hide all others
        this.values.forEach(g => {
            if (g.name === group.name) {
            g.show = !g.show; // toggle selected group
            } else {
            g.show = false; // collapse all others
            }
        });
      
    },
    getSelectedCount(groupName) {
        const originalGroup = this.values.find(g => g.name === groupName);
        if (!originalGroup) return 0;
        return originalGroup.list.filter(item => this.selectedValues.includes(item.value)).length;
    },
    async handleSearch(value) {
      if (this.lazyCaller) await this.$lazyCaller("search", value, 150);
      else this.search = value;
    },
    selectOption(item) {
      if (this.searchable) this.$refs.search.focus();

      const index = this.selectedValues.indexOf(item.value);
      if (index === -1) this.selectedValues.push(item.value);
      else this.selectedValues.splice(index, 1);

      if (this.noFooter) {
        this.$emit("update:selected", this.selectedValues);
        this.$emit("optionChanged", this.selectedValues);
      }
    },
    saveList(values) {
      const groupedSelected = this.values
        .map(group => ({
        name: group.name,
        list: group.list
            .filter(item => values.includes(item.value))
            .map(item => item.value)
        }))
        .filter(group => group.list.length > 0);
        
      this.$emit("update:selected", groupedSelected);
      this.$emit("optionChanged", groupedSelected);
      this.openDrop = false;
    },
    openDropContainer(event) {
      if (this.autoRotate) {
        const componentRect = event.target.getBoundingClientRect();
        const yPos = componentRect.top / window.innerHeight;
        this.rotated = yPos > 0.5;
      }

      this.openDrop = !this.openDrop;

      if (this.openDrop && this.searchable) {
        this.$nextTick(() => {
          this.$refs.search.focus();
        });
      }

      const reorderedList = JSON.parse(JSON.stringify(this.list));
      reorderedList.forEach(group => {
        if (group.show === undefined) group.show = false;
        const selected = group.list.filter(x => this.selected.includes(x.value));
        const rest = group.list.filter(x => !this.selected.includes(x.value));
        group.list = [...selected, ...rest];
      });

      this.values = reorderedList;

      this.flatList = reorderedList.flatMap(group =>
        group.list.map(item => ({ ...item, category: group.name }))
      );
    }
  },
  watch: {
    search() {},
    openDrop() {
        if (this.selected) {
            this.selectedValues = this.selected.flatMap(group =>
            Array.isArray(group?.list) ? group.list : []
            );
        }
        
      this.search = "";
    },
    selected: {
      handler(val) {
            const flat = Array.isArray(val)
            ? val.flatMap(group => Array.isArray(group?.list) ? group.list : [])
            : [];

            this.selectedValues = flat;
        },
      deep: true,
    }
  },
  computed: {
    computedItems() {
        if (!this.search) return this.values; // Return all groups if no search term

        // Map over all groups in this.values to preserve them
        return this.values.map(group => {
        const filteredList = this.flatList
            .filter(item => item.category === group.name && item.name.toLowerCase().includes(this.search.toLowerCase()))
            .map(item => ({
            name: item.name,
            value: item.value,
            color: item.color // Include other properties as needed
            }));

        return {
            name: group.name,
            list: filteredList,
            show: group.show // Preserve the show state
        };
        });
    },
    selectedAmount() {
      const totalItems = this.selected.reduce((sum, item) => sum + item.list.length, 0);
      return totalItems;
    },
  }
};
</script>

<style lang="scss" scoped>
.drop-multiple-header {
    width: auto;
    height: 35px;
    transition: all 0.2s ease-in-out;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.5px;
    position: relative;
    cursor: pointer;
    gap: 5px;

    &:hover .drop-title {
        background-color: #ededed;
    }

    &.rotate {  
        
        .select-input {
          order: 3;
        }
        .drop-list {
            order: 2;
        }
        .drop-actions {
            order: 1;
        }

        .drop-options {
            flex-direction: column-reverse;
            margin-bottom: 3px;
        }
    }

    &.active {
        box-shadow: 0 1.5px 5.8px 0 rgba(0, 0, 0, 0.17);
        z-index: 10000;

        &.rotate {
            .drop-title {
                border-radius: 0 0 5px 5px;
            }

            .drop-options {
                border-radius: 5px 5px 0 0;
            }
        }

        .drop-title {
            background-color: #ededed;
            border-color: white;
        }
    }

    .drop-title {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 0 10px 0 12px;
        border-radius: 5px;
        border: solid 2px #dbdbdb;
        transition: all 0.2s ease-in-out;
        background-color: #fff;
        height: 100%;

        svg {
            width: 20px;
        }

        span {
            overflow-x: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 100%;
        }
    }

    .drop-options {
        display: flex;
        flex-direction: column;
        z-index: 10000;
        position: absolute;
        left: 0;
        max-height: 350px;
        background: #fff;
        box-shadow: 0 1.5px 5.9px 0.7px rgba(0, 0, 0, 0.17);
        margin-top: 3px;
        border-radius: 5px;
        overflow-y: hidden;

        .drop-list {
            margin-top: 1px;
            min-height: 35px;
            // max-height: 230px;
            // overflow-y: auto;

            .no-results {
                height: 35px;
                display: flex;
                align-items: center;
                padding-left: 15px;
                font-size: 14px;
                color: #5f5f5f;
                font-weight: normal;
            }
        }
    }

    .drop-item {
        height: 35px;
        display: flex;
        align-items: center;
        padding: 0 10px;
        font-size: 14px;
        color: #5f5f5f;
        font-weight: normal;
        overflow-x: hidden;
        cursor: pointer;
        gap: 5px;
        line-height: 1.5;

        &.active {
            font-weight: 600;
        }

        span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: inline-block;
            max-width: 100%;
            vertical-align: top;
        }

        &:hover {
            background-color: #f4f4f4;
        }
    }

    .select-input {
        border: 2px solid #ededed;
        height: 35px;
        margin: 2px 2px;
        border-radius: 5px;

        &.custom {
            border-radius: 5px !important;
            border-top: 2px solid #ededed !important;
            margin: 2px 2px !important;
            height: 38px;
        }

        &:nth-child(2) {
            border-top: unset;
            margin: -2px 2px;
            border-radius: 0 0 5px 5px;
        }

        & .drop-item {
            padding: 0 5.5px;
        }
    }

    input {
        width: 100%;
        outline: none;
        border: 4px solid transparent;
        background: url(@/assets/svg/Icon_search.svg) no-repeat scroll 5px 3px;
        padding-left: 30px;
    }

}

.prop-name .color-dot{
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 5px;
}

.prop-name.active {
    font-weight: 600;
}

.select-svg {
    display: flex;
    align-items: center;
}

.drop-actions {
    box-shadow: 0 -2px 0 #ededed;
}

.drop-buttons {
    display: flex;
    justify-content: flex-end;
    padding: 8.5px 15px;
    gap: 15px;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: 0.5px;
    color: #8d8d8d;

    & button:nth-child(2) {
        font-weight: 600;
        color: #4195ff;
    }
}

.group-category {

    // if more than one and not first child
    &:not(:first-child) {
        box-shadow: 0 -2px 0 0 #ededed;
    }

  .category-title {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2px;

    color: #5f5f5f;
    font-weight: 600;
    padding: 5px 10px;
    font-size: 14px;
    font-weight: 600;

    border-top: 2px solid transparent;
    border-bottom: 2px solid #ededed;

    &:hover {
        background-color: #ededed;
    }

    .item-count {
        color: #8d8d8d;
        font-weight: 400;
    }
  }

  .category-items {
    overflow-y: auto;
    max-height: 165px;
  }
}

.selected-count{
   background-color: #4195FF;
   color: #fff;
   font-size: 12px;
   padding: 0 5px;
   height: 14px;
   line-height: 1.3;
   display: flex;
   align-self: center;
   border-radius: 4px;
   margin-right: 3px;
}

.v-enter-active,
.v-leave-active {
    transition: opacity 0.2s ease-in-out;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

:deep(.drop-multiple-header .drop-title svg) {
    transition: transform 0.2s ease-in-out;
}

:deep(.drop-multiple-header.active) {
    .drop-title svg.drop-icon {
        transform: rotate(180deg)
    }
}
</style>
<style lang="scss">

</style>